import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContracts';
import { fmtUSDT } from '../../utils/format';
import AdminSection from './AdminSection';

const Row = ({ label, value }) => (
  <div className="admin-wiring-row">
    <span className="lbl">{label}</span>
    <code>{value || '—'}</code>
  </div>
);

/**
 * Direct-pay model: 5% admin fees and ceiling overflow / ineligible income
 * are batched per-tx and AUTOMATICALLY flushed to the deductionWallet and
 * missingWallet at the end of every entry function. There is no manual
 * withdraw needed (or possible) — this section is informational.
 */
const AdminMissingPool = ({ role, refreshKey }) => {
  const { coreStorage } = useContracts();
  const [missingWallet, setMissingWallet] = useState('');
  const [deductionWallet, setDeductionWallet] = useState('');
  const [pending, setPending] = useState({ deduction: '0', missing: '0' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!coreStorage?.read) return;
      try {
        const [m, d, p] = await Promise.all([
          coreStorage.read.missingWallet().catch(() => ''),
          coreStorage.read.deductionWallet().catch(() => ''),
          coreStorage.read.getPendingTransfers().catch(() => ({ deduction: 0, missing: 0 }))
        ]);
        if (!cancelled) {
          setMissingWallet(m);
          setDeductionWallet(d);
          setPending({
            deduction: p.deduction.toString(),
            missing: p.missing.toString()
          });
        }
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [coreStorage, refreshKey]);

  return (
    <AdminSection
      title="Fee & Missing Pool Routing"
      subtitle="Auto-flushed to wallets at end of every transaction — no manual withdraw needed."
      role={role}
    >
      <div className="admin-grid-2">
        <div className="admin-tile yellow">
          <div className="admin-tile-title">Deduction Wallet</div>
          <div className="admin-tile-meta" style={{ marginTop: 4 }}>
            Receives the 5% admin fee from every income credit (sponsor, level, booster, royalty).
          </div>
          <Row label="Address" value={deductionWallet} />
          <div className="card-sub" style={{ marginTop: 10 }}>Pending (this tx)</div>
          <div className="card-value yellow">{fmtUSDT(pending.deduction)}</div>
          <div className="card-sub">USDT (will flush at tx end)</div>
        </div>

        <div className="admin-tile green">
          <div className="admin-tile-title">Missing Wallet</div>
          <div className="admin-tile-meta" style={{ marginTop: 4 }}>
            Receives ceiling-overflow + ineligible-recipient income + expired held entries.
          </div>
          <Row label="Address" value={missingWallet} />
          <div className="card-sub" style={{ marginTop: 10 }}>Pending (this tx)</div>
          <div className="card-value green">{fmtUSDT(pending.missing)}</div>
          <div className="card-sub">USDT (will flush at tx end)</div>
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
        Pending values are normally <strong>0</strong> — they only show non-zero if observed mid-transaction.
        Both wallets are configurable via <em>Ownership &amp; Roles</em> below (owner only).
      </div>
    </AdminSection>
  );
};

export default AdminMissingPool;
